import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import debounce from 'debounce-promise';
import { GithubPicker } from 'react-color';
import tinycolor from 'tinycolor2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faPalette,
  faPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { GAUGE, GAUGE_RANGE } from '../../Constants/WidgetTypes';
import TemplateService from '../../Services/TemplateService';
import { gaugeRangeColorOptions } from '../../Constants/ColorPaletteOptions';

export default memo(ConfigureGaugeWidget);

function ConfigureGaugeWidget({
  datapointsMap,
  gtemplateID,
  handleAddRange,
  handleColorInput,
  handleColorSelected,
  handleDatapointSelect,
  handleMinMaxValueUpdate,
  handleOptionDisabled,
  handleRangeValueUpdate,
  handleRemoveRange,
  handleRemoveWidgetSignal,
  handleSignalSelect,
  index,
  row,
  orgID,
  updateState,
  widgetType,
}) {
  const [showColorPicker, toggleColorPicker] = useState('');
  const [requestPending, setRequestPending] = useState(false);
  const isLegacy = widgetType === GAUGE;

  const cover = {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  };
  const service = useMemo(() => new TemplateService(), []);
  let source = CancelToken.source();

  const fetchOutputSignals = async (searchTerm) => {
    try {
      setRequestPending(true);
      const params = {
        limit: 100,
        orgID,
        search: 'name',
        searchTerm,
      };

      source.cancel();
      source = CancelToken.source();
      const { data } = await service.searchOutputSignals(
        gtemplateID,
        params,
        source.token
      );
      setRequestPending(false);
      return data.map((os) => ({ label: os.name, value: os }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error }));
      setRequestPending(false);
      return [];
    }
  };
  useEffect(() => source?.cancel, []);

  return (
    <div
      className={`row no-gutters align-items-center bg-background-gray rounded my-3 py-2${
        isLegacy ? ' py-md-0' : ''
      }`}
      style={{ minHeight: '5rem' }}
    >
      <div className="col-1 pl-1 px-md-3">
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon icon={faCircle} color="white" size="lg" />
          <span className="fa-layers-text signal-number-text font-weight-bold text-muted">
            {index + 1}
          </span>
        </span>
      </div>
      <div
        className={`px-md-0 col-10 d-flex flex-column flex-md-row${
          isLegacy ? '' : ' flex-md-wrap'
        }`}
      >
        <div
          className={
            isLegacy ? 'flex-grow-1 pr-md-2' : 'col-12 col-md-6 pl-md-0 pr-md-2'
          }
        >
          <label htmlFor={`signalCollection-${index}`}>Signal Collection</label>
          <Select
            captureMenuScroll={false}
            isSearchable
            maxMenuHeight={200}
            menuPortalTarget={document.body}
            name={`signalCollection-${index}`}
            options={datapointsMap}
            onChange={(e) => handleDatapointSelect(e, index)}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            value={row.datapoint}
          />
        </div>
        <div
          className={
            isLegacy ? 'flex-grow-1 px-md-2' : 'col-12 col-md-6 px-md-0'
          }
        >
          <label htmlFor={`signal-${index}`}>Signal to track</label>
          {row?.datapoint?.value === 'outputSignal' ? (
            <AsyncSelect
              cacheOptions
              captureMenuScroll={false}
              defaultOptions
              isLoading={requestPending}
              loadOptions={debounce(fetchOutputSignals, 1000)}
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              name={`signal-${index}`}
              onChange={(e) => handleSignalSelect(e, index)}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              value={row?.signal}
            />
          ) : (
            <Select
              captureMenuScroll={false}
              isDisabled={!row?.datapoint?.values?.length}
              isOptionDisabled={handleOptionDisabled}
              isSearchable
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              name={`signal-${index}`}
              options={row?.datapoint?.values || []}
              onChange={(e) => handleSignalSelect(e, index)}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              value={row.signal}
            />
          )}
        </div>
        <div className={isLegacy ? 'col-md-3 px-0 form-group mb-0' : 'd-none'}>
          <label className="text-nowrap" htmlFor="maxValue">
            Gauge Min/Max Value
          </label>
          <div className="row no-gutters align-items-center justify-content-between">
            <div className=" col-12 col-md-6 mb-1 mb-md-0 pr-md-1">
              <input
                className="form-control"
                name="minValue"
                onChange={(e) => handleMinMaxValueUpdate(e, index)}
                placeholder="Min"
                type="text"
                value={row.minValue}
              />
            </div>
            <div className="col-12 col-md-6 pl-md-1">
              <input
                className="form-control"
                name="maxValue"
                onChange={(e) => handleMinMaxValueUpdate(e, index)}
                placeholder="Max"
                type="text"
                value={row.maxValue}
              />
            </div>
          </div>
        </div>
        {isLegacy
          ? null
          : row.ranges?.map?.((range, i) => {
              const colorPreviewStyle = tinycolor(range?.color).isValid()
                ? { backgroundColor: range.color }
                : {};

              return (
                <div
                  className="row align-items-stretch my-2 mr-md-n5"
                  key={range?.id}
                >
                  <div className="col-1 d-flex align-items-center justify-content-center">
                    <span className="fa-layers fa-fw">
                      <FontAwesomeIcon icon={faCircle} color="white" />
                      <span className="fa-layers-text signal-number-text font-weight-bold text-muted">
                        {String.fromCharCode(97 + i)}
                      </span>
                    </span>
                  </div>
                  <div className="col-10 row flex-column flex-md-row">
                    <div className="col-12 col-md-3 pl-md-0">
                      <label className="text-nowrap" htmlFor="minValue">
                        Range Min Value
                      </label>
                      <input
                        className="form-control form-control-sm"
                        name="minValue"
                        onChange={(e) => handleRangeValueUpdate(e, index, i)}
                        readOnly={!!i}
                        type="text"
                        value={range?.minValue || ''}
                      />
                    </div>
                    <div className="col-12 col-md-3 pl-md-0">
                      <label className="text-nowrap" htmlFor="maxValue">
                        Range Max Value
                      </label>
                      <input
                        className="form-control form-control-sm"
                        name="maxValue"
                        onChange={(e) => handleRangeValueUpdate(e, index, i)}
                        type="text"
                        value={range?.maxValue || ''}
                      />
                    </div>
                    <div className="col-12 col-md-3 pl-md-0">
                      <label className="text-nowrap" htmlFor="label">
                        Range Label
                      </label>
                      <input
                        className="form-control form-control-sm"
                        name="label"
                        onChange={(e) => handleRangeValueUpdate(e, index, i)}
                        type="text"
                        value={range?.label || ''}
                      />
                    </div>
                    <div className="col-12 col-md-3 px-md-0">
                      <label className="text-nowrap" htmlFor="color">
                        Choose a Color
                      </label>
                      <div className="input-group">
                        <input
                          aria-describedby={`colorPickerAddon${index}-${i}`}
                          aria-label="choose range color"
                          className="form-control form-control-sm"
                          name="color"
                          onChange={(e) => handleColorInput(e, index, i)}
                          type="text"
                          value={range?.color}
                        />
                        <div className="input-group-append position-relative">
                          <button
                            className="btn btn-sm input-group-text shadow-none"
                            id={`colorPickerAddon${index}-${i}`}
                            onClick={() =>
                              toggleColorPicker(
                                showColorPicker === `${index}-${i}`
                                  ? ''
                                  : `${index}-${i}`
                              )
                            }
                            style={{ ...colorPreviewStyle }}
                          >
                            <FontAwesomeIcon
                              className={
                                range?.color ? 'd-none' : 'text-brand-primary'
                              }
                              icon={faPalette}
                            />
                          </button>
                          {showColorPicker === `${index}-${i}` ? (
                            <div
                              className="position-absolute"
                              style={{ right: 0, top: '3rem', zIndex: 10 }}
                            >
                              <div
                                aria-hidden={true}
                                onClick={() => toggleColorPicker('')}
                                style={cover}
                              />
                              <GithubPicker
                                colors={gaugeRangeColorOptions}
                                onChangeComplete={({ hex }) =>
                                  handleColorSelected(hex, index, i)
                                }
                                triangle="top-right"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-1 d-flex flex-column align-items-center justify-content-center">
                    <button
                      className="btn btn-link px-0 py-1"
                      onClick={() => handleRemoveRange(index, i)}
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-danger" />
                    </button>
                    <button
                      className={i >= 4 ? 'd-none' : 'btn btn-link px-0 py-1'}
                      onClick={() => handleAddRange(index, i)}
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        className="text-alert-confirmation"
                      />
                    </button>
                  </div>
                </div>
              );
            })}
      </div>
      <div className="col-1 pl-1 pl-md-4">
        <button
          className="btn btn-link p-0"
          onClick={() => handleRemoveWidgetSignal(index)}
        >
          <FontAwesomeIcon icon={faTimes} className="text-text-dark" />
        </button>
      </div>
    </div>
  );
}

ConfigureGaugeWidget.propTypes = {
  datapointsMap: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ),
  gtemplateID: PropTypes.string,
  handleAddRange: PropTypes.func,
  handleColorInput: PropTypes.func,
  handleColorSelected: PropTypes.func,
  handleDatapointSelect: PropTypes.func,
  handleMinMaxValueUpdate: PropTypes.func,
  handleOptionDisabled: PropTypes.func,
  handleRangeValueUpdate: PropTypes.func,
  handleRemoveRange: PropTypes.func,
  handleRemoveWidgetSignal: PropTypes.func,
  handleSignalSelect: PropTypes.func,
  index: PropTypes.number,
  orgID: PropTypes.string,
  row: PropTypes.shape({
    datapoint: PropTypes.shape({
      value: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.object),
    }),
    minValue: PropTypes.string,
    maxValue: PropTypes.string,
    ranges: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        color: PropTypes.string,
        label: PropTypes.string,
        maxValue: PropTypes.string,
        minValue: PropTypes.string,
      })
    ),
    signal: PropTypes.object,
  }),
  updateState: PropTypes.func,
  widgetType: PropTypes.oneOf([GAUGE, GAUGE_RANGE]),
};
